import { useEffect } from "react";
import { useAppDispatch } from "../store/hooks";
import {
  useGetLoggedInUserInfoQuery,
  useGetAllRolesQuery,
} from "../store/services/user";
import { setUserInfo, clearUserInfo } from "../store/slices/user/userSlice";
import { setRoles, clearRoles } from "../store/slices/roles/rolesSlice";
import {
  getUserManagementPermissions,
  getBusinessPartnersPermissions,
  getGSAPVolumesPermissions,
  getOMRPricePermissions,
  getGenerateReportPermissions,
} from "./permissions";

interface UseUserAuthProps {
  isLoggedIn: boolean;
  isLoading: boolean;
  hasError: boolean;
}

interface UseUserAuthReturn {
  userInfo: unknown;
  userInfoError: unknown;
  shouldShowUnauthorized: boolean;
  shouldShowSessionExpired: boolean;
  isLoadingUserInfo: boolean;
  isAdmin: boolean;
  canViewUserManagement: boolean;
  canAccessPartnerImport: boolean;
  canAccessGSAPImport: boolean;
  canAccessOMRAdditionalTabs: boolean;
  canAccessGenerateReport: boolean;
  userTypeName: string;
}

export const useUserAuth = ({
  isLoggedIn,
  isLoading,
  hasError,
}: UseUserAuthProps): UseUserAuthReturn => {
  const dispatch = useAppDispatch();

  // Use query hook with skip parameter to prevent premature calls
  const {
    data: userInfo,
    error: userInfoError,
    isLoading: isUpdating,
  } = useGetLoggedInUserInfoQuery(undefined, {
    skip: !isLoggedIn || isLoading || hasError,
  });

  // Fetch all roles - skip if not logged in
  const { data: rolesData } = useGetAllRolesQuery(undefined, {
    skip: !isLoggedIn || isLoading || hasError,
  });

  // Store user info in Redux when successfully received
  useEffect(() => {
    if (userInfo) {
      dispatch(setUserInfo(userInfo));
    }
  }, [userInfo, dispatch]);

  // Store roles in Redux when successfully received
  useEffect(() => {
    if (rolesData && rolesData.length > 0) {
      dispatch(setRoles(rolesData));
    }
  }, [rolesData, dispatch]);

  // Handle 403/401 Forbidden/Unauthorized response
  useEffect(() => {
    if (userInfoError) {
      const status =
        "status" in userInfoError && typeof userInfoError.status === "number"
          ? userInfoError.status
          : null;
      const originalStatus =
        "originalStatus" in userInfoError &&
        typeof userInfoError.originalStatus === "number"
          ? userInfoError.originalStatus
          : null;

      if (
        status === 403 ||
        status === 401 ||
        originalStatus === 403 ||
        originalStatus === 401
      ) {
        dispatch(clearUserInfo());
        dispatch(clearRoles());
      }
    }
  }, [userInfoError, dispatch]);

  const status =
    userInfoError &&
    "status" in userInfoError &&
    typeof userInfoError.status === "number"
      ? userInfoError.status
      : null;
  const originalStatus =
    userInfoError &&
    "originalStatus" in userInfoError &&
    typeof userInfoError.originalStatus === "number"
      ? userInfoError.originalStatus
      : null;

  const isForbidden = status === 403 || originalStatus === 403;
  const hasApiError = !!userInfoError;
  const isActive = userInfo?.isActive;
  // 403 or inactive user → Unauthorized page
  const shouldShowUnauthorized =
    (hasApiError && isForbidden) || (isLoggedIn && isActive === false);
  // Any other API error (401, 5xx, network, etc.) → SessionExpired page
  const shouldShowSessionExpired = hasApiError && !isForbidden;
  const isLoadingUserInfo = isLoggedIn && !userInfo && !userInfoError;

  const userTypeName = userInfo?.userTypeName || "";

  return {
    userInfo,
    userInfoError,
    shouldShowUnauthorized,
    shouldShowSessionExpired,
    isLoadingUserInfo: isUpdating || isLoadingUserInfo,
    isAdmin: userInfo?.roleId === 4,
    canViewUserManagement: getUserManagementPermissions(userInfo?.roleId || 0)
      .canView,
    canAccessPartnerImport: getBusinessPartnersPermissions(
      userInfo?.roleId || 0,
    ).canAdd,
    canAccessGSAPImport: getGSAPVolumesPermissions(userInfo?.roleId || 0)
      .canAdd,
    canAccessOMRAdditionalTabs: getOMRPricePermissions(userInfo?.roleId || 0)
      .canAdd,
    canAccessGenerateReport: getGenerateReportPermissions(userInfo?.roleId || 0)
      .canView,
    userTypeName,
  };
};
